import { useState } from "react";
import { api, ApiError } from "../api/client";
import { encryptShare } from "../crypto/otshare";
import { useToast } from "./toast-context";

// Create a one-time (burn-after-reading) share link. Fields can be prefilled from a vault item or
// entered by hand. Everything is encrypted in the browser (AES-256-GCM); the decryption key goes into
// the URL fragment, so the server stores only ciphertext + the non-secret label. An optional passphrase
// becomes a second factor the recipient must supply out of band.

export interface OneTimeShareSeed {
  label?: string;
  fields?: { label: string; value: string }[];
}

type Row = { label: string; value: string; include: boolean };

const TTL_OPTIONS: { label: string; seconds: number }[] = [
  { label: "1 hour", seconds: 3600 },
  { label: "24 hours", seconds: 86400 },
  { label: "7 days", seconds: 604800 },
];

export function CreateOneTimeShareDialog({ initial, onClose }: { initial: OneTimeShareSeed; onClose: () => void }) {
  const toast = useToast();
  const seeded = (initial.fields ?? []).filter((f) => f.value);
  const [rows, setRows] = useState<Row[]>(
    seeded.length ? seeded.map((f) => ({ label: f.label, value: f.value, include: true })) : [{ label: "", value: "", include: true }],
  );
  const [label, setLabel] = useState(initial.label ?? "");
  const [ttl, setTtl] = useState(86400);
  const [usePass, setUsePass] = useState(false);
  const [passphrase, setPassphrase] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);

  const setRow = (i: number, patch: Partial<Row>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, { label: "", value: "", include: true }]);
  const removeRow = (i: number) => setRows((rs) => (rs.length > 1 ? rs.filter((_, j) => j !== i) : rs));

  async function create() {
    const fields = rows
      .filter((r) => r.include && r.value.trim())
      .map((r) => ({ label: r.label.trim() || "value", value: r.value }));
    if (!fields.length) {
      setError("Add at least one value to share.");
      return;
    }
    if (usePass && !passphrase) {
      setError("Enter a passphrase or turn it off.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const enc = await encryptShare({ v: 1, fields }, usePass ? passphrase : undefined);
      const { id } = await api.createOneTimeShare({
        ciphertext: enc.ciphertextB64,
        passphrase_required: enc.passphraseRequired,
        label: label.trim() || undefined,
        ttl_seconds: ttl,
      });
      setLink(`${window.location.origin}/s/${id}#k=${enc.k}&s=${enc.s}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not create the share.");
    } finally {
      setBusy(false);
    }
  }

  const copyLink = () => {
    if (!link) return;
    navigator.clipboard.writeText(link).then(() => toast.success("Link copied")).catch(() => undefined);
  };

  return (
    <div className="fixed inset-0 grid place-items-center bg-black/50 px-4" onClick={onClose}>
      <div className="max-h-[90vh] w-[460px] overflow-auto rounded-md border border-border bg-panel shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-4 py-3 font-semibold">Share once</div>

        {link ? (
          <div className="flex flex-col gap-3 px-4 py-4 text-sm">
            <p className="text-xs text-muted">
              Send this link to the recipient. It reveals the details <strong>once</strong>, then burns.
              Anyone with the link can open it — treat it like the secret itself.
            </p>
            <div className="flex flex-col gap-1">
              <span className="text-xs uppercase tracking-wide text-muted">One-time link</span>
              <div className="flex gap-2">
                <input readOnly value={link} className="flex-1 rounded border border-border bg-panel2 px-2 py-1.5 font-mono text-xs" />
                <button onClick={copyLink} className="rounded bg-accent px-3 py-1.5 text-white hover:bg-accent2">
                  Copy
                </button>
              </div>
            </div>
            {usePass && (
              <div className="rounded border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn">
                Send the passphrase through a <strong>different</strong> channel than the link — that is what
                makes it a second factor.
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="rounded border border-border px-3 py-1.5 hover:border-accent">
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 px-4 py-4 text-sm">
            <p className="text-xs text-muted">
              The values below are encrypted in your browser. The key travels in the link, not to the
              server — so we can never read the shared secret.
            </p>

            <div className="flex flex-col gap-2">
              <span className="text-xs uppercase tracking-wide text-muted">Fields</span>
              {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={r.include}
                    onChange={(e) => setRow(i, { include: e.target.checked })}
                    title="Include this field"
                    className="accent-accent"
                  />
                  <input
                    value={r.label}
                    onChange={(e) => setRow(i, { label: e.target.value })}
                    placeholder="label"
                    className="w-28 rounded border border-border bg-panel2 px-2 py-1 text-xs"
                  />
                  <input
                    value={r.value}
                    onChange={(e) => setRow(i, { value: e.target.value })}
                    placeholder="value"
                    className="flex-1 rounded border border-border bg-panel2 px-2 py-1 font-mono text-xs"
                  />
                  <button onClick={() => removeRow(i)} title="Remove" className="rounded border border-border px-2 py-1 text-xs text-muted hover:border-danger hover:text-danger">
                    ✕
                  </button>
                </div>
              ))}
              <button onClick={addRow} className="self-start rounded border border-border px-2 py-1 text-xs hover:border-accent">
                + Add field
              </button>
            </div>

            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted">Label / hint (optional — shown before reveal, NOT encrypted)</span>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. BDO admin login"
                className="rounded border border-border bg-panel2 px-2 py-1.5"
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted">Expires after</span>
              <select value={ttl} onChange={(e) => setTtl(Number(e.target.value))} className="rounded border border-border bg-panel2 px-2 py-1.5">
                {TTL_OPTIONS.map((o) => (
                  <option key={o.seconds} value={o.seconds}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={usePass} onChange={(e) => setUsePass(e.target.checked)} className="accent-accent" />
              <span>Protect with a passphrase (second factor)</span>
            </label>
            {usePass && (
              <div className="flex gap-2">
                <input
                  type={showPass ? "text" : "password"}
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  placeholder="passphrase"
                  className="flex-1 rounded border border-border bg-panel2 px-2 py-1.5 font-mono"
                />
                <button type="button" onClick={() => setShowPass((v) => !v)} className="rounded border border-border px-2 text-xs hover:border-accent">
                  {showPass ? "Hide" : "Show"}
                </button>
              </div>
            )}

            {error && <div className="text-xs text-danger">{error}</div>}

            <div className="flex justify-end gap-2 border-t border-border pt-3">
              <button onClick={onClose} className="rounded border border-border px-3 py-1.5 hover:border-accent">
                Cancel
              </button>
              <button onClick={() => void create()} disabled={busy} className="rounded bg-accent px-3 py-1.5 font-medium text-white hover:bg-accent2 disabled:opacity-60">
                {busy ? "Creating…" : "Create link"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
